import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trash2,
  Shield,
  RefreshCw,
  Search,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Copy,
  Check,
  Smartphone,
  Globe,
  Eye,
  User,
  Building2,
  Phone,
  FileText,
  Calendar,
  X,
  Download,
  Mail,
  ShieldCheck,
  Lock,
  Send
} from 'lucide-react';
import backendApi from '../../services/backendApi';
import { useAuth } from '../../context/AuthContext';

interface DeletionRequestItem {
  id: string;
  ticket_id: string;
  full_name: string;
  email: string;
  company_name: string | null;
  phone: string | null;
  reason: string;
  additional_notes: string | null;
  status: 'PENDING' | 'IN_REVIEW' | 'COMPLETED' | 'REJECTED';
  status_display: string;
  source: 'WEB' | 'MOBILE_APP' | 'EMAIL' | 'PORTAL';
  source_display: string;
  client_ip: string | null;
  user_agent: string | null;
  admin_notes: string | null;
  resolved_by: number | null;
  resolved_by_email: string | null;
  requested_at: string;
  updated_at: string;
  resolved_at: string | null;
}

interface RequestStats {
  total: number;
  pending: number;
  in_review: number;
  completed: number;
  rejected: number;
}

const DataDeletionRequests: React.FC = () => {
  const { user: currentUser } = useAuth();

  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<DeletionRequestItem[]>([]);
  const [stats, setStats] = useState<RequestStats>({
    total: 0,
    pending: 0,
    in_review: 0,
    completed: 0,
    rejected: 0,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [selectedSource, setSelectedSource] = useState<string>('ALL');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [copiedTicket, setCopiedTicket] = useState<string | null>(null);
  const [copiedEmail, setCopiedEmail] = useState(false);

  // Review Modal State
  const [selectedItem, setSelectedItem] = useState<DeletionRequestItem | null>(null);
  const [modalStatus, setModalStatus] = useState<string>('PENDING');
  const [modalAdminNotes, setModalAdminNotes] = useState<string>('');
  const [updating, setUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchRequests = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await backendApi.getDataDeletionRequests({
        page,
        page_size: 20,
        status: selectedStatus,
        source: selectedSource,
        search: searchQuery.trim(),
      });

      if (res && res.success) {
        setRequests(res.results || []);
        if (res.stats) {
          setStats(res.stats);
        }
        setTotalPages(res.total_pages || 1);
      } else {
        setFetchError(res?.message || 'Unable to load deletion requests.');
      }
    } catch (err: any) {
      console.error('Failed to load data deletion requests:', err);
      setFetchError(err?.message || 'Network error occurred while fetching requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [page, selectedStatus, selectedSource]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchRequests();
  };

  const handleCopyTicket = (ticketId: string) => {
    navigator.clipboard.writeText(ticketId);
    setCopiedTicket(ticketId);
    setTimeout(() => setCopiedTicket(null), 2000);
  };

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const openReviewModal = (item: DeletionRequestItem) => {
    setSelectedItem(item);
    setModalStatus(item.status);
    setModalAdminNotes(item.admin_notes || '');
    setUpdateMessage(null);
  };

  const handleUpdateStatus = async () => {
    if (!selectedItem) return;
    setUpdating(true);
    setUpdateMessage(null);

    try {
      const res = await backendApi.updateDataDeletionRequest(selectedItem.ticket_id, {
        status: modalStatus,
        admin_notes: modalAdminNotes,
      });

      if (res && res.success) {
        setUpdateMessage({ type: 'success', text: 'Request status and compliance audit notes saved successfully.' });
        // Update local state in table
        setRequests((prev) =>
          prev.map((r) => (r.ticket_id === selectedItem.ticket_id ? res.data : r))
        );
        setSelectedItem(res.data);
        // Refresh KPI cards
        fetchRequests();
      } else {
        setUpdateMessage({ type: 'error', text: res?.message || 'Failed to update request.' });
      }
    } catch (err: any) {
      setUpdateMessage({ type: 'error', text: err.message || 'Error updating request.' });
    } finally {
      setUpdating(false);
    }
  };

  const handleExportCSV = () => {
    if (requests.length === 0) return;
    const headers = ['Ticket ID', 'Requester Name', 'Email', 'Company', 'Phone', 'Reason', 'Source', 'Status', 'Requested At', 'Resolved At', 'Admin Notes'];
    const rows = requests.map((r) => [
      r.ticket_id,
      `"${(r.full_name || '').replace(/"/g, '""')}"`,
      r.email,
      `"${(r.company_name || '').replace(/"/g, '""')}"`,
      r.phone || '',
      `"${(r.reason || '').replace(/"/g, '""')}"`,
      r.source,
      r.status,
      r.requested_at,
      r.resolved_at || '',
      `"${(r.admin_notes || '').replace(/"/g, '""')}"`,
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `axonmetric_deletion_requests_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return (
          <span
            style={{ whiteSpace: 'nowrap' }}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-amber-50 text-amber-950 border border-amber-400 shadow-2xs whitespace-nowrap flex-nowrap shrink-0"
          >
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0"></span>
            Pending Review
          </span>
        );
      case 'IN_REVIEW':
        return (
          <span
            style={{ whiteSpace: 'nowrap' }}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-[#F2F8FC] text-[#0D2747] border border-[#3EA3DA] shadow-2xs whitespace-nowrap flex-nowrap shrink-0"
          >
            <Clock className="w-3.5 h-3.5 text-[#1C4D88] stroke-[2.5] shrink-0" />
            Under Review
          </span>
        );
      case 'COMPLETED':
        return (
          <span
            style={{ whiteSpace: 'nowrap' }}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-emerald-50 text-emerald-950 border border-emerald-400 shadow-2xs whitespace-nowrap flex-nowrap shrink-0"
          >
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 stroke-[2.5] shrink-0" />
            Completed / Erased
          </span>
        );
      case 'REJECTED':
        return (
          <span
            style={{ whiteSpace: 'nowrap' }}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-rose-50 text-rose-950 border border-rose-400 shadow-2xs whitespace-nowrap flex-nowrap shrink-0"
          >
            <XCircle className="w-3.5 h-3.5 text-rose-700 stroke-[2.5] shrink-0" />
            Rejected
          </span>
        );
      default:
        return <span className="text-xs font-black text-slate-700 whitespace-nowrap">{status}</span>;
    }
  };

  const getSourceBadge = (source: string) => {
    switch (source) {
      case 'MOBILE_APP':
        return (
          <span
            style={{ whiteSpace: 'nowrap' }}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-black bg-[#F2F8FC] text-[#0D2747] border border-[#8AC4E6] shadow-2xs whitespace-nowrap flex-nowrap shrink-0"
          >
            <Smartphone className="w-3.5 h-3.5 text-[#1C4D88] stroke-[2.5] shrink-0" /> Mobile App
          </span>
        );
      case 'WEB':
        return (
          <span
            style={{ whiteSpace: 'nowrap' }}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-black bg-[#F2F8FC] text-[#0D2747] border border-[#8AC4E6] shadow-2xs whitespace-nowrap flex-nowrap shrink-0"
          >
            <Globe className="w-3.5 h-3.5 text-[#1C4D88] stroke-[2.5] shrink-0" /> Web Portal
          </span>
        );
      default:
        return (
          <span
            style={{ whiteSpace: 'nowrap' }}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-black bg-slate-100 text-slate-900 border border-slate-300 whitespace-nowrap flex-nowrap shrink-0"
          >
            {source}
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen pt-16 lg:pt-3 pb-10 px-3 sm:px-5 font-sans text-slate-900">
      <div className="max-w-7xl mx-auto space-y-4">
        
        {/* Top Hero Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-4 sm:p-5 rounded-xl bg-white border border-slate-200 shadow-xs">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 border border-red-300 text-red-900 text-xs font-black shadow-xs">
                <Shield className="w-3.5 h-3.5 text-red-700 stroke-[2.5]" />
                <span>Store Compliance & Privacy Governance (GDPR / CCPA)</span>
              </div>
              <div className="flex items-center gap-3 pt-0.5">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1C4D88] to-[#0B1E38] flex items-center justify-center text-white shadow-xs shrink-0">
                  <Trash2 size={20} className="stroke-[2.5]" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                    Data Deletion & Erasure Requests
                  </h1>
                  <p className="text-xs sm:text-sm font-bold text-slate-700 mt-0.5">
                    Track and execute statutory account deletion requests submitted across web and mobile apps.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={handleExportCSV}
                disabled={requests.length === 0}
                className="px-3.5 py-2 rounded-lg border border-slate-300 hover:border-slate-400 bg-white text-slate-900 hover:bg-slate-50 text-xs sm:text-sm font-black transition-all inline-flex items-center gap-1.5 shadow-xs disabled:opacity-40"
              >
                <Download size={15} className="stroke-[2.5]" />
                Export CSV
              </button>
              <button
                type="button"
                onClick={fetchRequests}
                disabled={loading}
                className="px-4 py-2 rounded-lg bg-[#1C4D88] hover:bg-[#153a66] text-white text-xs sm:text-sm font-black transition-all inline-flex items-center gap-1.5 shadow-xs hover:shadow-sm disabled:opacity-50"
              >
                <RefreshCw size={15} className={`stroke-[2.5] ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </motion.div>

        {/* Error Alert Banner */}
        {fetchError && (
          <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-300 text-rose-950 text-xs sm:text-sm flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-2.5">
              <AlertCircle className="w-5 h-5 shrink-0 text-rose-700 stroke-[2.5]" />
              <div>
                <p className="font-black text-sm">Error Loading Requests</p>
                <p className="text-xs font-bold text-rose-800 mt-0.5">{fetchError}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={fetchRequests}
              className="px-3.5 py-1.5 bg-rose-700 hover:bg-rose-800 text-white font-black rounded-lg transition-colors shadow-xs text-xs"
            >
              Retry
            </button>
          </div>
        )}

        {/* 4 KPI Metric Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-black text-slate-600 uppercase tracking-wider">Total Inquiries</p>
              <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mt-1">{stats.total}</h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-800 shrink-0">
              <FileText size={20} className="stroke-[2.5]" />
            </div>
          </div>

          <div className="p-4 rounded-xl bg-gradient-to-br from-white to-amber-50/80 border border-amber-300 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-black text-amber-900 uppercase tracking-wider">Pending Review</p>
              <h3 className="text-2xl sm:text-3xl font-black text-amber-950 mt-1">{stats.pending}</h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-amber-200/80 flex items-center justify-center text-amber-950 shrink-0">
              <Clock size={20} className="stroke-[2.5]" />
            </div>
          </div>

          <div className="p-4 rounded-xl bg-gradient-to-br from-white to-[#F2F8FC]/80 border border-[#8AC4E6] shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-black text-[#0D2747] uppercase tracking-wider">Under Review</p>
              <h3 className="text-2xl sm:text-3xl font-black text-[#0D2747] mt-1">{stats.in_review}</h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-[#C2DFF1]/80 flex items-center justify-center text-[#0D2747] shrink-0">
              <Eye size={20} className="stroke-[2.5]" />
            </div>
          </div>

          <div className="p-4 rounded-xl bg-gradient-to-br from-white to-emerald-50/80 border border-emerald-300 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-black text-emerald-900 uppercase tracking-wider">Erased / Resolved</p>
              <h3 className="text-2xl sm:text-3xl font-black text-emerald-950 mt-1">{stats.completed}</h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-200/80 flex items-center justify-center text-emerald-950 shrink-0">
              <CheckCircle2 size={20} className="stroke-[2.5]" />
            </div>
          </div>
        </div>

        {/* Filters and Search Toolbar */}
        <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-xs space-y-3">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            {/* Status Tabs */}
            <div className="flex flex-wrap items-center gap-1 p-1 bg-slate-100 rounded-lg border border-slate-200">
              {[
                { label: 'All Requests', val: 'ALL' },
                { label: 'Pending', val: 'PENDING' },
                { label: 'Under Review', val: 'IN_REVIEW' },
                { label: 'Completed', val: 'COMPLETED' },
                { label: 'Rejected', val: 'REJECTED' },
              ].map((tab) => (
                <button
                  key={tab.val}
                  type="button"
                  onClick={() => {
                    setSelectedStatus(tab.val);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-black transition-all ${
                    selectedStatus === tab.val
                      ? 'bg-white text-[#1C4D88] shadow-xs border border-slate-300'
                      : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200/50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Source Filter Dropdown */}
            <div className="flex items-center gap-2">
              <select
                value={selectedSource}
                onChange={(e) => {
                  setSelectedSource(e.target.value);
                  setPage(1);
                }}
                className="text-xs sm:text-sm font-black px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1C4D88]/30 shadow-2xs cursor-pointer"
              >
                <option value="ALL">All Sources</option>
                <option value="WEB">Web Portal</option>
                <option value="MOBILE_APP">Mobile App</option>
                <option value="EMAIL">Direct Email</option>
                <option value="PORTAL">Internal Portal</option>
              </select>
            </div>
          </div>

          {/* Search Input */}
          <form onSubmit={handleSearchSubmit} className="relative flex items-center">
            <Search className="w-4 h-4 text-slate-500 absolute left-3.5 stroke-[2.5]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by ticket ID (AXN-DEL-...), email, name, or company..."
              className="w-full pl-10 pr-24 py-2 text-xs sm:text-sm font-bold text-slate-900 bg-slate-50 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C4D88]/30 focus:border-[#1C4D88] transition-all placeholder:text-slate-500"
            />
            <button
              type="submit"
              className="absolute right-1.5 px-4 py-1.5 bg-[#1C4D88] hover:bg-[#153a66] text-white rounded-md text-xs sm:text-sm font-black transition-colors shadow-2xs"
            >
              Search
            </button>
          </form>
        </div>

        {/* Requests Table */}
        <div className="rounded-xl bg-white border border-slate-200 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[980px] border-collapse">
              <thead className="bg-slate-100/90 border-b border-slate-300 text-slate-900 font-black text-xs uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4 whitespace-nowrap w-[170px]">Ticket ID</th>
                  <th className="py-3 px-4 whitespace-nowrap w-[220px]">Requester</th>
                  <th className="py-3 px-4 whitespace-nowrap w-[160px]">Organization</th>
                  <th className="py-3 px-4 min-w-[180px]">Reason</th>
                  <th className="py-3 px-4 whitespace-nowrap w-[140px]">Source</th>
                  <th className="py-3 px-4 whitespace-nowrap w-[170px]">Requested At</th>
                  <th className="py-3 px-4 whitespace-nowrap w-[160px]">Status</th>
                  <th className="py-3 px-4 text-right whitespace-nowrap w-[110px]">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-slate-900">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="py-10 text-center text-slate-700 font-black text-sm">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-[#1C4D88]" />
                      Loading deletion requests...
                    </td>
                  </tr>
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-10 text-center text-slate-600 font-bold text-sm">
                      <Trash2 className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                      No data deletion requests match your current filters.
                    </td>
                  </tr>
                ) : (
                  requests.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                      {/* Ticket ID: Strictly on a single line, never wraps */}
                      <td className="py-3 px-4 whitespace-nowrap align-middle">
                        <div className="inline-flex items-center gap-1.5 whitespace-nowrap">
                          <span
                            style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}
                            className="font-mono font-black text-xs sm:text-sm text-[#1C4D88] tracking-tight bg-[#F2F8FC]/90 px-2.5 py-1 rounded-md border border-[#C2DFF1] whitespace-nowrap inline-block select-all"
                          >
                            {item.ticket_id}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCopyTicket(item.ticket_id)}
                            title="Copy ticket ID" aria-label="Copy ticket ID"
                            className="p-1 hover:bg-slate-200 rounded text-slate-500 hover:text-slate-800 transition-colors shrink-0"
                          >
                            {copiedTicket === item.ticket_id ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600 stroke-[3]" />
                            ) : (
                              <Copy className="w-3.5 h-3.5 stroke-[2.5]" />
                            )}
                          </button>
                        </div>
                      </td>

                      {/* Requester: High contrast, neat vertical spacing */}
                      <td className="py-3 px-4 align-middle">
                        <div className="font-black text-xs sm:text-sm text-slate-900 leading-tight whitespace-nowrap">
                          {item.full_name}
                        </div>
                        <div className="text-xs font-semibold text-slate-600 mt-0.5 whitespace-nowrap">
                          {item.email}
                        </div>
                        {item.phone && (
                          <div className="text-xs font-medium text-slate-500 mt-0.5 whitespace-nowrap">
                            {item.phone}
                          </div>
                        )}
                      </td>

                      {/* Organization: Crisp & Bold, whitespace-nowrap */}
                      <td className="py-3 px-4 align-middle whitespace-nowrap">
                        {item.company_name ? (
                          <span className="text-xs sm:text-sm font-bold text-slate-900">
                            {item.company_name}
                          </span>
                        ) : (
                          <span className="text-[11px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-2.5 py-0.5 rounded-md inline-block uppercase tracking-wider">
                            Not Specified
                          </span>
                        )}
                      </td>

                      {/* Reason */}
                      <td className="py-3 px-4 align-middle max-w-xs">
                        <span className="font-bold text-xs sm:text-sm text-slate-800 line-clamp-2" title={item.reason}>
                          {item.reason}
                        </span>
                      </td>

                      {/* Source: Never wraps */}
                      <td className="py-3 px-4 align-middle whitespace-nowrap">
                        {getSourceBadge(item.source)}
                      </td>

                      {/* Requested At */}
                      <td className="py-3 px-4 align-middle text-xs font-bold text-slate-700 whitespace-nowrap">
                        {new Date(item.requested_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>

                      {/* Status: Clean pill, never wraps */}
                      <td className="py-3 px-4 align-middle whitespace-nowrap">
                        {getStatusBadge(item.status)}
                      </td>

                      {/* Review Action Button */}
                      <td className="py-3 px-4 align-middle text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openReviewModal(item)}
                          className="px-3.5 py-1.5 rounded-lg bg-[#1C4D88] hover:bg-[#153a66] text-white font-black text-xs transition-all inline-flex items-center gap-1.5 shadow-2xs hover:shadow-xs"
                        >
                          <Eye className="w-3.5 h-3.5 stroke-[2.5]" /> Review
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {totalPages > 1 && (
            <div className="py-3 px-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs font-bold">
              <span className="text-slate-700 font-bold">
                Page <strong className="text-slate-900 font-black">{page}</strong> of <strong className="text-slate-900 font-black">{totalPages}</strong>
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 font-black text-slate-800 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 font-black text-slate-800 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* High-Fidelity Review & Management Modal with Crisp Bold Typography */}
      {/* ========================================================================= */}
      <AnimatePresence>
        {selectedItem && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-5 bg-slate-950/80 backdrop-blur-sm overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 15 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-xl w-full my-auto flex flex-col overflow-hidden"
            >
              {/* Premium Brand Header Banner */}
              <div className="relative bg-gradient-to-r from-[#0B1E38] via-[#1C4D88] to-[#0B1E38] text-white px-5 py-3.5 shrink-0 overflow-hidden">
                <div className="absolute -right-8 -top-8 w-36 h-36 bg-[#00A3FF]/20 rounded-full blur-xl pointer-events-none"></div>

                <div className="relative z-10 flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        style={{ whiteSpace: 'nowrap' }}
                        className="font-mono text-xs font-black tracking-wider px-2.5 py-0.5 rounded bg-white/15 border border-white/25 text-[#00A3FF] whitespace-nowrap shrink-0"
                      >
                        {selectedItem.ticket_id}
                      </span>
                      {getSourceBadge(selectedItem.source)}
                    </div>
                    <h3 className="text-lg sm:text-xl font-black text-white tracking-tight">
                      Account & Data Erasure Review
                    </h3>
                    <p className="text-xs font-bold text-white/80">
                      Submitted on {new Date(selectedItem.requested_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setSelectedItem(null)}
                    className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                  >
                    <X size={18} className="stroke-[2.5]" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-4 sm:p-5 overflow-y-auto space-y-3.5 text-xs sm:text-sm text-slate-900">
                
                {/* Feedback Notification Alert */}
                {updateMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-3 rounded-xl border flex items-center gap-2.5 shadow-2xs ${
                      updateMessage.type === 'success'
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-950'
                        : 'bg-rose-50 border-rose-300 text-rose-950'
                    }`}
                  >
                    {updateMessage.type === 'success' ? (
                      <CheckCircle2 size={18} className="text-emerald-700 shrink-0 stroke-[2.5]" />
                    ) : (
                      <AlertCircle size={18} className="text-rose-700 shrink-0 stroke-[2.5]" />
                    )}
                    <span className="font-black text-xs sm:text-sm">{updateMessage.text}</span>
                  </motion.div>
                )}

                {/* 2-Column Bento Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  
                  {/* Requester Identity Card */}
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 space-y-2 flex flex-col justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-black text-slate-600 uppercase tracking-wider">
                      <User size={14} className="text-slate-800 stroke-[2.5]" />
                      Requester Identity
                    </div>
                    
                    <div className="space-y-1">
                      <div>
                        <div className="text-sm sm:text-base font-black text-slate-900">{selectedItem.full_name}</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Mail size={13} className="text-slate-600 shrink-0 stroke-[2.5]" />
                          <span className="text-xs sm:text-sm font-bold text-slate-800">{selectedItem.email}</span>
                          <button
                            type="button"
                            onClick={() => handleCopyEmail(selectedItem.email)}
                            title="Copy email" aria-label="Copy email"
                            className="p-0.5 hover:bg-slate-200 rounded text-slate-600 hover:text-slate-900 transition-colors"
                          >
                            {copiedEmail ? <Check size={13} className="text-emerald-600 stroke-[3]" /> : <Copy size={13} className="stroke-[2.5]" />}
                          </button>
                        </div>
                      </div>

                      {selectedItem.phone && (
                        <div className="flex items-center gap-1.5 text-slate-800 text-xs font-bold pt-1 border-t border-slate-200">
                          <Phone size={13} className="text-slate-600 shrink-0 stroke-[2.5]" />
                          <span>{selectedItem.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Organization & Scope Card */}
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 space-y-2 flex flex-col justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-black text-slate-600 uppercase tracking-wider">
                      <Building2 size={14} className="text-slate-800 stroke-[2.5]" />
                      Scope & Network Audit
                    </div>

                    <div className="space-y-1">
                      <div>
                        <div className="text-[11px] font-black text-slate-500 uppercase tracking-wider">Organization / Plant</div>
                        <div className="text-xs sm:text-sm font-black text-slate-900 mt-0.5">
                          {selectedItem.company_name || 'Individual Engineer Account'}
                        </div>
                      </div>

                      <div className="pt-1.5 border-t border-slate-200 flex items-center justify-between text-[11px] sm:text-xs font-bold text-slate-700">
                        <span>Client IP: <strong className="text-slate-950 font-black">{selectedItem.client_ip || '127.0.0.1'}</strong></span>
                        <span>SLA: <strong className="text-amber-900 font-black">30 Days</strong></span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Deletion Reason & Remarks Card */}
                <div className="p-3.5 rounded-xl bg-amber-50/70 border border-amber-300 space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs font-black text-amber-900 uppercase tracking-wider">
                    <Trash2 size={14} className="text-amber-800 stroke-[2.5]" />
                    Stated Deletion Reason
                  </div>
                  <p className="text-xs sm:text-sm font-black text-amber-950">
                    {selectedItem.reason}
                  </p>
                  
                  {selectedItem.additional_notes && (
                    <div className="mt-1.5 pt-1.5 border-t border-amber-300">
                      <div className="text-[11px] font-black text-amber-900 uppercase">User Notes:</div>
                      <p className="text-xs font-bold text-amber-950 italic mt-0.5 whitespace-pre-wrap">
                        "{selectedItem.additional_notes}"
                      </p>
                    </div>
                  )}
                </div>

                {/* Status Transition Control */}
                <div className="space-y-2 pt-0.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs sm:text-sm font-black text-slate-900 uppercase tracking-wide">
                      Update Request Status
                    </label>
                    <span className="text-xs font-black text-slate-700 flex items-center gap-1.5">
                      <span>Current:</span>
                      {getStatusBadge(selectedItem.status)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      {
                        id: 'PENDING',
                        label: 'Pending',
                        desc: 'Queued for audit',
                        icon: Clock,
                        iconColor: 'text-amber-600',
                        activeBg: 'bg-amber-500 text-white border-amber-600 ring-2 ring-amber-300',
                        inactiveBorder: 'border-slate-300 text-slate-900 hover:border-amber-400 hover:bg-amber-50/40'
                      },
                      {
                        id: 'IN_REVIEW',
                        label: 'Under Review',
                        desc: 'Verifying identity',
                        icon: Eye,
                        iconColor: 'text-[#0066A3]',
                        activeBg: 'bg-[#0066A3] text-white border-[#1C4D88] ring-2 ring-[#8AC4E6]',
                        inactiveBorder: 'border-slate-300 text-slate-900 hover:border-[#3EA3DA] hover:bg-[#F2F8FC]/40'
                      },
                      {
                        id: 'COMPLETED',
                        label: 'Completed',
                        desc: 'Data scrubbed',
                        icon: CheckCircle2,
                        iconColor: 'text-emerald-600',
                        activeBg: 'bg-emerald-600 text-white border-emerald-700 ring-2 ring-emerald-300',
                        inactiveBorder: 'border-slate-300 text-slate-900 hover:border-emerald-400 hover:bg-emerald-50/40'
                      },
                      {
                        id: 'REJECTED',
                        label: 'Rejected',
                        desc: 'Disallowed request',
                        icon: XCircle,
                        iconColor: 'text-rose-600',
                        activeBg: 'bg-rose-600 text-white border-rose-700 ring-2 ring-rose-300',
                        inactiveBorder: 'border-slate-300 text-slate-900 hover:border-rose-400 hover:bg-rose-50/40'
                      },
                    ].map((item) => {
                      const Icon = item.icon;
                      const isSelected = modalStatus === item.id;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setModalStatus(item.id)}
                          className={`p-2.5 rounded-xl border-2 text-left transition-all relative overflow-hidden ${
                            isSelected
                              ? `${item.activeBg} shadow-xs`
                              : `bg-white ${item.inactiveBorder}`
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <Icon size={16} className={`stroke-[2.5] ${isSelected ? 'text-white' : item.iconColor}`} />
                            {isSelected && <Check size={14} className="stroke-[3]" />}
                          </div>
                          <div className={`font-black text-xs sm:text-sm leading-tight ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                            {item.label}
                          </div>
                          <div className={`text-[11px] mt-0.5 font-bold leading-tight ${isSelected ? 'text-white/95' : 'text-slate-600'}`}>
                            {item.desc}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Internal Compliance Notes */}
                <div className="space-y-1 pt-0.5">
                  <label className="block text-xs sm:text-sm font-black text-slate-900 uppercase tracking-wide">
                    Internal Compliance & Audit Log Notes
                  </label>
                  <textarea
                    rows={2}
                    value={modalAdminNotes}
                    onChange={(e) => setModalAdminNotes(e.target.value)}
                    placeholder="Document identity verification findings, database scrub confirmation, or reason for rejection..."
                    className="w-full text-xs sm:text-sm font-bold text-slate-900 px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1C4D88]/30 focus:border-[#1C4D88] transition-all placeholder:text-slate-500"
                  />
                  <p className="text-[11px] font-bold text-slate-500">
                    These notes are recorded in the internal compliance audit log and are only visible to authorized administrators.
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-slate-100 border-t border-slate-200 px-5 py-3 flex flex-col sm:flex-row items-center justify-between gap-2.5 shrink-0">
                <div className="flex items-center gap-1.5 text-xs font-black text-slate-700">
                  <Lock size={14} className="text-slate-600 stroke-[2.5]" />
                  <span>Statutory Record Retention Cycle Active</span>
                </div>

                <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
                  <button
                    type="button"
                    onClick={() => setSelectedItem(null)}
                    className="px-4 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-900 text-xs sm:text-sm font-black transition-all shadow-2xs"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={handleUpdateStatus}
                    disabled={updating}
                    className="px-5 py-1.5 rounded-lg bg-[#1C4D88] hover:bg-[#153a66] text-white text-xs sm:text-sm font-black transition-all shadow-xs hover:shadow-sm disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {updating ? (
                      <>
                        <RefreshCw size={14} className="animate-spin stroke-[2.5]" /> Saving...
                      </>
                    ) : (
                      <>
                        <Send size={14} className="stroke-[2.5]" /> Save & Apply Status
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DataDeletionRequests;
